/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Box, Typography } from '@mui/material';
import type { TFunction } from 'i18next';
import React from 'react';
import { AlertBanner, ConfirmationDialog } from '@opengeoweb/shared';
import { useMutateDeleteDrawing } from '../../api/warning-api/hooks';
import { useWarningsTranslation } from '../../utils/i18n';

export const getDeleteDescription = (
  drawingName: string,
  t: TFunction,
): string => `${t('delete-dialog-description')} "${drawingName}"?`;

export const getDeleteSucces = (drawingName: string, t: TFunction): string =>
  `Drawing "${drawingName}" ${t('delete-succes')}`;

export const ConfirmDeleteDialog: React.FC<{
  drawingId: string;
  drawingName: string;
  onDeleteSucces: (drawingId: string) => void;
  onClose: () => void;
}> = ({
  drawingId,
  drawingName,
  onDeleteSucces = (): void => {},
  onClose = (): void => {},
}) => {
  const { t } = useWarningsTranslation();

  const { mutate: deleteDrawing, isPending, error } = useMutateDeleteDrawing();

  const onSubmit = (): void => {
    deleteDrawing(
      { drawingId, objectName: drawingName },
      {
        onSuccess: () => {
          onDeleteSucces(drawingId);
        },
      },
    );
  };

  const description = getDeleteDescription(drawingName, t);

  return (
    <ConfirmationDialog
      cancelLabel={t('delete-dialog-cancel')}
      confirmLabel={t('delete-dialog-confirm')}
      description={!error ? description : ''}
      isLoading={isPending}
      onClose={onClose}
      onSubmit={onSubmit}
      open
      title={t('delete-dialog-title')}
      {...(error?.message && {
        content: (
          <Box sx={{ marginBottom: 0 }}>
            <AlertBanner title={error.message} />
            <Typography sx={{ marginTop: 1 }} variant="body1">
              {description}
            </Typography>
          </Box>
        ),
      })}
    />
  );
};
